import { useEffect, useMemo, useState } from 'react'
import { JANELA_AMPLA_DIAS, JANELA_DIAS, monthsToScan, reconcile } from '@domain/receipt'
import type { Receipt } from '@domain/receipt'
import type { Transaction } from '@domain/finance'
import { addDays, daysBetween } from '@shared/date/civil'
import { formatBRL } from '@shared/money/format'
import { currentReferenceMonth } from '@shared/date/timezone'
import { normalizedText } from '@domain/product'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { SeloPendente } from '@ui/finance/SeloPendente'

/**
 * Escolher a transação de uma nota que subiu solta.
 *
 * ## A conciliação SUGERE, e a pessoa decide
 *
 * `Receipt.md`: *"Uma candidata → sugere. Nenhuma ou várias → o usuário
 * escolhe."* `reconcile` devolve todas as candidatas com o mesmo valor, e esta
 * tela **nunca vincula sozinha** — nem quando há uma só. Uma nota grudada na
 * transação errada não avisa que errou: ela vira o detalhamento de uma compra
 * que não é aquela, e o número fica plausível.
 *
 * ## Duas camadas de sugestão, e não uma
 *
 * Os três dias de `Receipt.md` continuam sendo a janela forte — dentro dela, o
 * mesmo valor quase sempre é a mesma compra. Mas a tela lia só esses três dias
 * e, quando a transação estava a duas semanas (cupom fotografado tarde, fatura
 * que só chegou depois), ela dizia "nenhuma parecida" e jogava a pessoa numa
 * lista de dezenas de linhas — para achar, na mão, a que tinha exatamente o
 * mesmo valor. O trabalho que a máquina se recusava a fazer era trivial.
 *
 * Então: até três dias a tela **sugere**; de quatro a trinta ela **oferece**,
 * dizendo em voz alta a distância em dias. A segunda camada é mais fraca de
 * propósito — o mesmo valor a três semanas de distância é coincidência com
 * frequência real, e quem confere a data é quem viu o cupom.
 *
 * ## A lista completa começa FECHADA
 *
 * Ela é o escape, não o caminho. Aberta, ocupava a tela inteira com dezenas de
 * saídas e enterrava a sugestão — e quando não havia sugestão nenhuma, a
 * primeira coisa que a pessoa via era um paredão sem instrução.
 *
 * Fechada, ela vira uma linha. E a linha ganha a cor da marca exatamente
 * quando é ela que precisa ser tocada — nenhuma candidata, nenhum caminho
 * automático. Havendo sugestão, a ação está acima e a linha fica apagada: a
 * mesma regra do selo de status em Notas — cor só para o que pede alguma coisa.
 */
export function VincularTransacao({
  nota,
  onVincular,
}: {
  nota: Receipt
  /**
   * A transação INTEIRA, e não só o id: quem chama precisa saber se ela já tem
   * categoria antes de aplicar a da nota, e a lista já a tem em mãos — pedir o
   * id obrigaria a reler do banco o que está aqui do lado.
   */
  onVincular: (transacao: Transaction) => void
}) {
  const { transactionRepository } = useFinanceRepositories()
  const [transacoes, setTransacoes] = useState<Transaction[] | 'carregando' | 'falhou'>(
    'carregando',
  )
  const [busca, setBusca] = useState('')

  // A data sai do objeto ANTES do `useMemo`: com o encadeamento opcional na
  // lista de dependências, o compilador do React não consegue provar que o
  // corpo depende só dela.
  const emitidaEm = nota.extracted?.issuedAt ?? null
  const meses = useMemo(
    () =>
      emitidaEm === null
        ? [currentReferenceMonth()]
        : monthsToScan(emitidaEm, addDays, JANELA_AMPLA_DIAS),
    [emitidaEm],
  )

  useEffect(() => {
    let vivo = true
    void Promise.all(meses.map((m) => transactionRepository.listByCompetence(m)))
      .then((porMes) => {
        if (!vivo) return
        // Um mês pode aparecer nas duas leituras se a nota estiver na borda; o
        // `Map` por id evita a mesma transação listada duas vezes.
        const unicas = new Map(porMes.flat().map((t) => [t.id, t]))
        setTransacoes([...unicas.values()])
      })
      .catch((e: unknown) => {
        console.error('VincularTransacao: a leitura falhou', e)
        if (vivo) setTransacoes('falhou')
      })
    return () => {
      vivo = false
    }
  }, [transactionRepository, meses])

  if (transacoes === 'carregando') {
    return <p className="text-sm text-[var(--text-muted)]">Procurando a transação…</p>
  }
  if (transacoes === 'falhou') {
    return <p className="text-sm">Não foi possível carregar as transações.</p>
  }

  // Só saídas: uma nota fiscal detalha uma compra, e oferecer o salário como
  // candidato seria ruído puro.
  const saidas = transacoes.filter((t) => t.source.amountCents < 0 && t.status !== 'canceled')

  // UMA chamada, com a janela larga, e o corte em dois pela distância. Chamar
  // `reconcile` duas vezes daria o mesmo resultado hoje, mas nada garantiria
  // que a lista curta continua sendo subconjunto da longa — aqui garante.
  const comMesmoValor =
    nota.extracted === null
      ? []
      : reconcile(
          nota.extracted,
          saidas.map((t) => ({
            id: t.id,
            amountCents: t.source.amountCents,
            transactionDate: t.source.transactionDate,
          })),
          daysBetween,
          JANELA_AMPLA_DIAS,
        )
  const certeiras = comMesmoValor.filter((c) => c.daysApart <= JANELA_DIAS)
  const possiveis = comMesmoValor.filter((c) => c.daysApart > JANELA_DIAS)
  const porId = new Map(saidas.map((t) => [t.id, t]))

  const restantes = saidas.filter((t) => !comMesmoValor.some((c) => c.transactionId === t.id))
  const termo = normalizedText(busca)
  const outras = restantes
    .filter((t) => termo === '' || normalizedText(t.source.description).includes(termo))
    .sort((a, b) => b.source.transactionDate.localeCompare(a.source.transactionDate))

  const semSugestao = comMesmoValor.length === 0

  return (
    <div>
      {certeiras.length > 0 && (
        <>
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            {certeiras.length === 1
              ? 'Uma transação bate com o valor e a data desta nota:'
              : `${String(certeiras.length)} transações batem com o valor e a data desta nota:`}
          </p>
          <ul aria-label="Transações sugeridas" className="mb-3">
            {certeiras.map((c) => (
              <ItemDeCandidata
                key={c.transactionId}
                transaction={porId.get(c.transactionId)}
                detalhe={c.daysApart === 0 ? 'mesmo dia' : `${String(c.daysApart)}d de diferença`}
                onVincular={onVincular}
              />
            ))}
          </ul>
        </>
      )}

      {possiveis.length > 0 && (
        <>
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            {certeiras.length > 0
              ? 'Mesmo valor, mas longe da data da nota:'
              : `Nada nos ${String(JANELA_DIAS)} dias em volta. Estas têm o mesmo valor, mais longe — confira a data:`}
          </p>
          <ul aria-label="Mesmo valor, mais longe" className="mb-3">
            {possiveis.map((c) => (
              <ItemDeCandidata
                key={c.transactionId}
                transaction={porId.get(c.transactionId)}
                // A distância vai por extenso: é ela que decide se a coincidência
                // de valor vale alguma coisa, e "23d" some no meio da linha.
                detalhe={`${String(c.daysApart)} dias de diferença`}
                onVincular={onVincular}
              />
            ))}
          </ul>
        </>
      )}

      {semSugestao && (
        <p className="mb-2 text-xs text-[var(--text-muted)]">
          {/* O campo que faltou, e não "valor ou data": a frase juntava as duas
              condições e aparecia com o total lido à vista no fechamento
              (decisão 309). */}
          {faltou(nota) ?? `Nenhuma saída com o mesmo valor nos últimos ${String(JANELA_AMPLA_DIAS)} dias.`}
        </p>
      )}

      <details className="group border-t border-[var(--border)]">
        <summary
          className={`alvo flex w-full cursor-pointer list-none items-center justify-between gap-2 text-sm ${
            semSugestao ? 'text-[var(--brand)]' : 'text-[var(--text-muted)]'
          }`}
        >
          <span>{semSugestao ? 'Escolher a transação' : 'Nenhuma dessas — ver as outras'}</span>
          <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
            {restantes.length === 1 ? '1 saída' : `${String(restantes.length)} saídas`}
            {/* A seta é a única pista de que isto abre — mesma lição da Dobra. */}
            <span aria-hidden="true" className="transition-transform group-open:rotate-90">
              ›
            </span>
          </span>
        </summary>

        <div className="pb-1">
          <label className="mb-2 block text-sm">
            <span className="sr-only">Buscar transação</span>
            <input
              type="search"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value)
              }}
              placeholder={`Buscar entre as saídas de ${meses.map(mesLegivel).join(', ')}`}
              className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
            />
          </label>
          {outras.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">
              Nenhuma saída encontrada. A transação pode ainda não ter chegado do banco — o sync
              roda uma vez por dia.
            </p>
          ) : (
            <ul aria-label="Todas as transações" className="max-h-80 overflow-y-auto">
              {outras.map((t) => (
                <li key={t.id}>
                  <Escolha
                    transaction={t}
                    detalhe={null}
                    onEscolher={() => {
                      onVincular(t)
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </div>
  )
}

/**
 * Uma candidata na lista.
 *
 * A transação pode não estar no mapa se sumir entre a leitura e o desenho; o
 * `<li>` some junto, e não uma linha vazia com um botão que não vincula nada.
 */
function ItemDeCandidata({
  transaction,
  detalhe,
  onVincular,
}: {
  transaction: Transaction | undefined
  detalhe: string
  /**
   * A transação INTEIRA, e não só o id: quem chama precisa saber se ela já tem
   * categoria antes de aplicar a da nota, e a lista já a tem em mãos — pedir o
   * id obrigaria a reler do banco o que está aqui do lado.
   */
  onVincular: (transacao: Transaction) => void
}) {
  if (transaction === undefined) return null
  return (
    <li>
      <Escolha
        transaction={transaction}
        detalhe={detalhe}
        onEscolher={() => {
          onVincular(transaction)
        }}
      />
    </li>
  )
}

function Escolha({
  transaction,
  detalhe,
  onEscolher,
}: {
  transaction: Transaction
  detalhe: string | null
  onEscolher: () => void
}) {
  return (
    <button
      type="button"
      onClick={onEscolher}
      className="flex w-full items-baseline gap-2 border-b border-[var(--border)] py-2 text-left text-sm"
      aria-label={`Vincular a ${transaction.source.description}`}
    >
      <span className="text-[var(--text-muted)]">{diaEMes(transaction.source.transactionDate)}</span>
      <span className="min-w-0 flex-1 truncate">{transaction.source.description}</span>
      <SeloPendente status={transaction.status} />
      {detalhe !== null && (
        <span className="shrink-0 text-xs text-[var(--text-muted)]">{detalhe}</span>
      )}
      <span className="tabular-nums">{formatBRL(transaction.source.amountCents)}</span>
    </button>
  )
}

/** `'2026-08-12'` → `'12/08'`. */
function diaEMes(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`
}

/** `'2026-08'` → `'08/2026'`. */
function mesLegivel(mes: string): string {
  return `${mes.slice(5, 7)}/${mes.slice(0, 4)}`
}

/** O que a leitura não achou para sugerir a transação — ou `null`. */
function faltou(nota: Receipt): string | null {
  const semValor = nota.extracted?.totalCents == null
  const semData = nota.extracted?.issuedAt == null
  if (semValor && semData) return 'A leitura não achou o valor nem a data da nota, então não dá para sugerir.'
  if (semValor) return 'A leitura não achou o valor da nota, então não dá para sugerir.'
  if (semData) return 'A leitura não achou a data da nota, então não dá para sugerir.'
  return null
}
